import { Box, Flex, Heading, Spacer } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Navigate } from 'react-router';
import { Link } from 'react-router-dom';

import { FundingRequestStatusEnumGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { PermissionGuard, usePermissionGuard } from '~/core/PermissionGuard';
import { permissions as createFundingRequestPermissions } from '~/core/routes/funding-requests/create/_index';
import { permissions as verificationsPermissions } from '~/core/routes/funding-requests/verifications/Router';
import { UploadDrawerButton } from '~/packages/dataPipeline/ui/uploads/UploadDrawerButton';
import { FundingRequestHistoryViewContainer } from '~/packages/funding-requests/ui/history/ViewContainer';
import { CreateFundingRequestButton } from '~/packages/funding-requests/ui/submit/CreateRequest/CreateRequestButton';
import { VerificationTrackerTable } from '~/packages/verifications/ui/VerificationTrackerTable';

// Constants
const PRE_SUBMISSION_STATUSES: FundingRequestStatusEnumGQLType[] = [
  'pending',
  // 'unsubmitted', // don't currently show unsubmitted requests, only the current pending one
];
const POST_SUBMISSION_STATUSES: FundingRequestStatusEnumGQLType[] = [
  'approved',
  'submitted',
  'canceled',
  'expired',
  'rejected',
];

// Fragments
const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const { facilities } = useCreditFacilitiesOrThrow();
  const canSubmitFundingRequest = usePermissionGuard(
    createFundingRequestPermissions,
  );

  const allowedStatuses = useMemo(
    () =>
      canSubmitFundingRequest === 'allow'
        ? [...PRE_SUBMISSION_STATUSES, ...POST_SUBMISSION_STATUSES]
        : POST_SUBMISSION_STATUSES,
    [canSubmitFundingRequest],
  );

  return (
    <>
      <Flex>
        <Heading as="h2" mb="36px">
          Funding Requests
        </Heading>
        <Spacer />
        <Flex gap="0.5rem">
          <PermissionGuard
            permissions={(context) => {
              return verificationsPermissions(context);
            }}
          >
            <UploadDrawerButton buttonText="Start Verification" />
          </PermissionGuard>

          <PermissionGuard permissions={createFundingRequestPermissions}>
            <CreateFundingRequestButton />
          </PermissionGuard>
        </Flex>
      </Flex>
      <PermissionGuard permissions={verificationsPermissions}>
        <Box>
          <VerificationTrackerTable organizationId={user.organization.id} />
          <Box height="32px" />
        </Box>
      </PermissionGuard>

      <FundingRequestHistoryViewContainer
        allowedStatuses={allowedStatuses}
        activeCreditFacilities={facilities}
      />
      <PermissionGuard permissions={verificationsPermissions}>
        <Box mt="32px" mb="16px" textAlign="right">
          <Link to="/funding-requests/verifications/pipeline/history">
            View all verification reports
          </Link>
        </Box>
      </PermissionGuard>
    </>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="/" />;
    }}
  >
    <Flex direction="column" padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}>
      <RouteBase />
    </Flex>
  </RouteErrorBoundary>
);
